/**
 * Batch edit modal for bulk operations on selected images.
 */
import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui';
import { useBatchMoveImages, useBatchTagImages } from '@/hooks/queries/useMediaQueries';
import type { MediaFolder, MediaTag } from '@/types/media';

interface BatchEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedImageIds: string[];
  folders: MediaFolder[];
  tags: MediaTag[];
  onSuccess: () => void;
}

export function BatchEditModal({
  isOpen,
  onClose,
  selectedImageIds,
  folders,
  tags,
  onSuccess,
}: BatchEditModalProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [moveToFolder, setMoveToFolder] = useState(false);
  const [addTags, setAddTags] = useState(false);

  const batchMoveMutation = useBatchMoveImages();
  const batchTagMutation = useBatchTagImages();

  const handleSave = async () => {
    try {
      if (moveToFolder) {
        await batchMoveMutation.mutateAsync({
          imageIds: selectedImageIds,
          folderId: selectedFolderId || null,
        });
      }

      if (addTags && selectedTagIds.length > 0) {
        await batchTagMutation.mutateAsync({
          imageIds: selectedImageIds,
          tagIds: selectedTagIds,
        });
      }

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Batch edit error:', error);
    }
  };

  const handleClose = () => {
    setSelectedFolderId('');
    setSelectedTagIds([]);
    setMoveToFolder(false);
    setAddTags(false);
    onClose();
  };

  const isLoading = batchMoveMutation.isPending || batchTagMutation.isPending;
  const hasChanges = (moveToFolder) || (addTags && selectedTagIds.length > 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              Edit {selectedImageIds.length} Image{selectedImageIds.length !== 1 ? 's' : ''}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 space-y-6">
            {/* Move to folder section */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={moveToFolder}
                  onChange={(e) => setMoveToFolder(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Move to folder</span>
              </label>
              {moveToFolder && (
                <div className="mt-2 ml-6">
                  <select
                    value={selectedFolderId}
                    onChange={(e) => setSelectedFolderId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Root (No folder)</option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.path}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Add tags section */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={addTags}
                  onChange={(e) => setAddTags(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Add tags</span>
              </label>
              {addTags && (
                <div className="mt-2 ml-6">
                  <p className="text-xs text-gray-500 mb-2">
                    Click tags to add to selected images
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() =>
                            setSelectedTagIds((prev) =>
                              isSelected
                                ? prev.filter((id) => id !== tag.id)
                                : [...prev, tag.id]
                            )
                          }
                          className={`
                            px-2.5 py-1 rounded-full text-xs font-medium transition-all border
                            ${isSelected
                              ? 'border-transparent'
                              : 'border-gray-300 hover:border-gray-400'}
                          `}
                          style={isSelected ? {
                            backgroundColor: `${tag.color}20`,
                            color: tag.color,
                          } : {
                            backgroundColor: '#f3f4f6',
                            color: '#9ca3af',
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                    {tags.length === 0 && (
                      <p className="text-sm text-gray-400">No tags available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading || !hasChanges}
            >
              {isLoading ? 'Saving...' : 'Apply Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
